import React, { useEffect, useState } from 'react';
import {
  Col,
  Form,
  Row,
  Spinner,
} from 'react-bootstrap';
import useMassMailForm from 'utils/hooks/mailer/useMassMailForm';
import { useClosure, useHandleRequestErrors } from 'utils/hooks';
import { ResponseError } from 'services/api/base';
import { useListMailerProductsQuery } from 'services/api/mailer';
import { MASS_MAIL_BODY_TYPE_OPTIONS } from 'helper/mailer';
import Checkbox from 'components/DesignSystem/Checkbox';
import { selectMailerLayoutIsDanger, setIsDanger } from 'redux/reducers/mailerLayoutReducer';
import { store } from 'redux/store';
import { useSelector } from 'react-redux';
import RichTextEditor from 'components/RichTextEditor';
import Selector from 'components/DesignSystem/Selector';
import Button from 'components/DesignSystem/Button';
import CloseButton from '../../../../CloseButton';
import AlertsCollection from '../../../../AlertsCollection';
import CompanySelect from '../../ReportForm/CompanySelect';
import MassMailPreview from '../Preview';
import './index.scss';

export function checkElementSelection(idArray: number[], selectedId: number) {
  return idArray.includes(selectedId);
}

export function switchElement(
  idArray: number[],
  selectedId: number,
  switcher: (ids: number[]) => void,
) {
  return () => {
    const index = idArray.findIndex((element) => element === selectedId);
    if (index >= 0) {
      switcher(idArray.filter((element) => element !== selectedId));
    } else {
      switcher([...idArray, selectedId]);
    }
  };
}

export function switchAllElements(
  idArray: number[],
  selectedIds: number[],
  switcher: (ids: number[]) => void,
) {
  return () => {
    if (selectedIds.length === idArray.length) {
      switcher([]);
    } else {
      switcher(idArray);
    }
  };
}

export default function MassEmailNew({ close }: { close: () => void }) {
  const {
    alerts,
    productIds: selectedProductIds,
    setProductIds: setSelectedProductIds,
    fileRef,
    updateFile,
    body,
    setBody,
    bodyType,
    setBodyType,
    showPreview,
    setShowPreview,
    isTest,
    setIsTest,
    companyIds,
    setCompanyIds,
    showCompanySelect,
    previewButtonDisabled,
    submissionDisabled,
    clearInputsDisabled,
    clearInputs,
    massMailPreviewParams,
    submitMassMail,
    canCancelSubmitMassMail,
    cancelSubmitMassMail,
    companies,
    isLoading,
    subject,
    setSubject,
  } = useMassMailForm();
  const [validated, setValidated] = useState(false);
  const showNoProductsWarning = selectedProductIds.length === 0 && validated;
  const isDanger = useSelector(selectMailerLayoutIsDanger);
  const headerClass = isDanger ? 'mass-email-header-danger' : 'mass-email-header';
  const {
    data: productsResponse,
    isLoading: productsLoading,
    error: productsError,
  } = useListMailerProductsQuery();
  const products = productsResponse?.data || [];
  const productIds = products?.map((product) => (product.id));

  useHandleRequestErrors(productsError as ResponseError);

  const submitForm = (event: React.FormEvent) => {
    event.preventDefault();
    setValidated(true);
    if (!submissionDisabled) {
      submitMassMail();
    }
  };

  useEffect(() => {
    store.dispatch(setIsDanger(!isTest));
    return () => {
      store.dispatch(setIsDanger(false));
    };
  }, [isTest]);

  return (
    <Form onSubmit={submitForm} noValidate validated={validated}>
      <div className={`p-3 mb-3 d-flex justify-content-between align-items-end ${headerClass}`}>
        <CloseButton
          id="close-new-report"
          label="Novo E-mail em massa"
          onClick={close}
        />
        <div className="mx-4 d-flex">
          <Checkbox className="me-2" checked={isTest} onChange={() => setIsTest(!isTest)} />
          Envio de testes
        </div>
      </div>
      <AlertsCollection alerts={alerts} />
      <div className="mt-3 mx-3">
        <Row className="align-items-start">
          <Col>
            Selecione os produtos
          </Col>
        </Row>
        {productsLoading && <Spinner />}
        {!productsLoading && (
          <>
            <Row className="align-items-start mt-3">
              <Col>
                <Checkbox
                  className="me-2"
                  label="Todos"
                  checked={selectedProductIds.length === products?.length}
                  onChange={switchAllElements(
                    productIds,
                    selectedProductIds,
                    setSelectedProductIds,
                  )}
                />
              </Col>
            </Row>
            <Row className="align-items-start mt-1">
              {products?.map((product) => (
                <Col xs={6} md={2} key={product.id.toString()}>
                  <Checkbox
                    className="me-2 mt-2"
                    id={product.id.toString()}
                    label={product.label}
                    checked={checkElementSelection(selectedProductIds, product.id)}
                    onChange={switchElement(selectedProductIds, product.id, setSelectedProductIds)}
                  />
                </Col>
              ))}
              {showNoProductsWarning && (
                <div className="no-products-error-message mt-1">
                  Selecione ao menos um produto
                </div>
              )}
            </Row>
          </>
        )}
        <Row className="mb-4 my-3 align-items-end">
          <Col xs={12} md={3} className="mb-3 mb-md-0">
            <Form.Control
              multiple
              type="file"
              className="rounded-0"
              ref={fileRef}
              onChange={updateFile}
            />
            <Form.Control.Feedback type="invalid">
              Selecione ao menos um arquivo
            </Form.Control.Feedback>
          </Col>
          <Col xs={12} md={3} className="mb-3 mb-md-0">
            <Selector
              label="Corpo do E-mail"
              value={bodyType}
              setValue={setBodyType}
              validated={validated}
              isValid={bodyType && bodyType.length > 0}
              error="Selecione o tipo de corpo"
            >
              {MASS_MAIL_BODY_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Selector>
            <input type="hidden" value={bodyType || ''} />
          </Col>
          <Col xs={12} md={6} className="text-start text-md-end pt-1 pe-md-5">
            <Button
              icon="visible"
              disabled={previewButtonDisabled}
              onClick={() => setShowPreview(true)}
            >
              Visualizar
            </Button>
          </Col>
        </Row>
      </div>
      <div className="mx-3 mt-2">
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3" controlId="exampleForm.ControlTextarea1">
              <Form.Label> Assunto </Form.Label>
              <Form.Control
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
              />
              <Form.Control.Feedback type="invalid">
                Não deixe o assunto do email em branco
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      </div>
      <div className="mt-3 mx-3">
        <Form.Group className="mb-3" controlId="exampleForm.ControlTextarea1">
          <Form.Label> Mensagem </Form.Label>
          <RichTextEditor
            value={body}
            onChange={useClosure(setBody)}
          />
        </Form.Group>
        <Form.Control.Feedback type="invalid">
          Não deixe o corpo do email em branco
        </Form.Control.Feedback>
      </div>
      <div className="mt-4 mx-3 pb-3">
        <div className="d-flex flex-wrap gap-2">
          <Button
            type="secondary"
            disabled={clearInputsDisabled}
            onClick={clearInputs}
          >
            Limpar
          </Button>
          <Button
            disabled={submissionDisabled || canCancelSubmitMassMail}
            submit
          >
            {canCancelSubmitMassMail && (<Spinner className="me-2" size="sm" />)}
            Enviar
          </Button>
          <Button
            disabled={!canCancelSubmitMassMail}
            onClick={cancelSubmitMassMail}
          >
            Cancelar
          </Button>
        </div>
      </div>
      {showCompanySelect && (
        <div className="px-3">
          <CompanySelect
            companies={companies}
            companiesAreLoading={isLoading}
            selectedCompanyIds={companyIds}
            setSelectedCompanyIds={setCompanyIds}
          />
        </div>
      )}
      <MassMailPreview
        show={showPreview}
        handleClose={() => setShowPreview(false)}
        previewParams={massMailPreviewParams}
      />
    </Form>
  );
}
